/** @jsxImportSource @emotion/react */
import React, { useReducer, useState, useEffect } from "react";
import moment from "moment";
import {
  FormGroup,
  FormControl,
  FormLabel,
  FormText,
  Button,
  FormCheck,
  Alert,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import TimePicker from "rc-time-picker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus } from "@fortawesome/pro-solid-svg-icons";
import _ from "lodash";

import { Modal } from "components/molecules/Modal.molecule";
import { FormButton } from "components-old/modal-elems";
import { getCurrentDateForInput } from "utils/date-time";
import { MediaQueries } from "components/responsive";
import { ReportDTO } from "pages/reports/bi-dashboard-next/models/ReportDTO";
import {
  useEmailSchedule,
  EmailScheduleDTO,
  EmailScheduleDetails,
  DuplicateScheduleError,
} from "pages/reports/bi-dashboard-next/hooks/useEmailSchedule";
import { DialogModal } from "components/molecules/DialogModal.molecule";

const formatEmail = (emails: string): string[] => {
  return emails.split(";").map((singleEmail) => singleEmail.trim());
};

const normalizeTimeFormat = (times: string[]): string[] => {
  return times.map((time) => {
    if (time && time.length === 5 && time.includes(":")) {
      return `${time}:00`;
    }
    return time;
  });
};

const getStateWeekdays = (weekdays: string[] = []): string[] => {
  return weekdays
    .map((weekdayName) => {
      switch (weekdayName) {
        case "Sunday":
          return "0";
        case "Monday":
          return "1";
        case "Tuesday":
          return "2";
        case "Wednesday":
          return "3";
        case "Thursday":
          return "4";
        case "Friday":
          return "5";
        case "Saturday":
          return "6";
        default:
          return "";
      }
    })
    .filter(Boolean);
};

const getRequestWeekdays = (weekdays: string[] = []): string[] => {
  return weekdays
    .map((weekdayNum) => {
      switch (weekdayNum) {
        case "0":
          return "Sunday";
        case "1":
          return "Monday";
        case "2":
          return "Tuesday";
        case "3":
          return "Wednesday";
        case "4":
          return "Thursday";
        case "5":
          return "Friday";
        case "6":
          return "Saturday";
        default:
          return "";
      }
    })
    .filter(Boolean);
};

interface FormData {
  name: string;
  email: string;
  body: string;
  pattern: string;
  repeat: string;
  repeatMonth: string;
  weekday: string[];
  weekNum: string;
  onDay: string;
  time: string[];
  dateFrom: string;
  dateTo: string;
  paused: boolean;
}

type FormDataAction =
  | { type: "NAME"; payload: string }
  | { type: "EMAIL"; payload: string }
  | { type: "BODY"; payload: string }
  | { type: "PATTERN"; payload: string }
  | { type: "REPEAT"; payload: string }
  | { type: "ON_DAY"; payload: string }
  | { type: "DATE_FROM"; payload: string }
  | { type: "DATE_TO"; payload: string }
  | { type: "WEEK_NUM"; payload: string }
  | { type: "TIME"; payload: { index: number; time: string } }
  | { type: "ADD_TIME" }
  | { type: "REMOVE_TIME"; payload: { index: number } }
  | { type: "ADD_WEEKDAY"; payload: { value: string } }
  | { type: "REMOVE_WEEKDAY"; payload: { value: string } }
  | { type: "PAUSE"; payload: boolean }
  | { type: "LOAD_SCHEDULE"; payload: EmailScheduleDTO }
  | { type: "RESET"; payload: FormData };

const formDataReducer = (state: FormData, action: FormDataAction): FormData => {
  switch (action.type) {
    case "NAME":
      return { ...state, name: action.payload };

    case "EMAIL":
      return { ...state, email: action.payload };

    case "BODY":
      return { ...state, body: action.payload };

    case "PATTERN":
      return { ...state, pattern: action.payload };

    case "REPEAT":
      if (
        state.pattern === "yearly_day" ||
        state.pattern === "yearly_weekday"
      ) {
        return { ...state, repeatMonth: action.payload };
      }
      return { ...state, repeat: action.payload };

    case "ON_DAY":
      return { ...state, onDay: action.payload };

    case "DATE_FROM":
      if (moment(action.payload).isAfter(state.dateTo)) {
        return { ...state, dateFrom: action.payload, dateTo: "" };
      }

      return { ...state, dateFrom: action.payload };

    case "DATE_TO":
      return { ...state, dateTo: action.payload };

    case "WEEK_NUM":
      return { ...state, weekNum: action.payload };

    case "TIME": {
      let updatedTimes = [...state.time];
      updatedTimes[action.payload.index] = action.payload.time;
      return { ...state, time: updatedTimes };
    }
    case "ADD_TIME":
      return { ...state, time: [...state.time, "00:00:00"] };

    case "REMOVE_TIME": {
      let updatedTimes = [...state.time];
      updatedTimes.splice(action.payload.index, 1);
      return { ...state, time: updatedTimes };
    }
    case "ADD_WEEKDAY":
      const indexOfDay = state.weekday.indexOf(action.payload.value);
      if (indexOfDay === -1) {
        const updatedWeekdays = [...state.weekday, action.payload.value].sort();
        return { ...state, weekday: updatedWeekdays };
      } else {
        return state;
      }
    case "REMOVE_WEEKDAY": {
      const indexOfDay = state.weekday.indexOf(action.payload.value);
      if (indexOfDay !== -1) {
        let updatedWeekdays = [...state.weekday];
        updatedWeekdays.splice(indexOfDay, 1);
        return { ...state, weekday: updatedWeekdays };
      } else {
        return state;
      }
    }
    case "PAUSE":
      return { ...state, paused: action.payload };

    case "LOAD_SCHEDULE":
      const details =
        action.payload.details || action.payload.patternDetails || {};
      return {
        name: action.payload.name || "",
        email: action.payload.email ? action.payload.email.join("; ") : "",
        body: action.payload.emailBody || action.payload.body || "",
        pattern: details.pattern || "daily",
        repeat: details.repeat || "1",
        weekday: getStateWeekdays(details.weekday || []),
        weekNum: details.weekNum || "1",
        onDay: details.dayOfMonth || "1",
        time: normalizeTimeFormat(details.time || ["00:00:00"]),
        dateFrom: details.dateFrom || getCurrentDateForInput(),
        dateTo: details.dateTo || "",
        paused: !action.payload.enabled,
        repeatMonth: details.repeatMonth || "0",
      };
    case "RESET":
      return action.payload;
    default:
      return state;
  }
};

const getInitialScheduleState = (): FormData => {
  return {
    name: "",
    email: "",
    body: "",
    pattern: "daily",
    repeat: "1",
    repeatMonth: "0",
    time: ["00:00:00"],
    weekday: [],
    onDay: "1",
    weekNum: "1",
    dateFrom: getCurrentDateForInput(),
    dateTo: "",
    paused: false,
  };
};

const getPatternDetails = (data: FormData): EmailScheduleDetails => {
  switch (data.pattern) {
    case "daily":
      return {
        pattern: "daily",
        repeat: data.repeat,
        time: data.time,
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
      };

    case "weekly":
      return {
        pattern: "weekly",
        repeat: data.repeat,
        time: data.time,
        weekday: getRequestWeekdays(data.weekday),
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
      };

    case "monthly_day":
      return {
        pattern: "monthly_day",
        repeat: data.repeat,
        time: data.time,
        dayOfMonth: data.onDay,
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
      };

    case "monthly_weekday":
      return {
        pattern: "monthly_weekday",
        repeat: data.repeat,
        time: data.time,
        weekNum: data.weekNum,
        weekday: getRequestWeekdays(data.weekday),
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
      };

    case "yearly_day":
      return {
        pattern: "yearly_day",
        repeatMonth: data.repeatMonth,
        time: data.time,
        dayOfMonth: data.onDay,
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
      };

    case "yearly_weekday":
      return {
        pattern: "yearly_weekday",
        repeatMonth: data.repeatMonth,
        time: data.time,
        weekNum: data.weekNum,
        weekday: getRequestWeekdays(data.weekday),
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
      };

    default:
      return {
        pattern: "daily",
        time: data.time,
        dateFrom: data.dateFrom,
      };
  }
};

const validateScheduleForm = (data: FormData, t: (key: string) => string) => {
  if (!data.name) {
    return { error: true, errorText: t(`reports:"Schedule Name" is required`) };
  } else if (!data.email) {
    return { error: true, errorText: t(`reports:"Send to" is required`) };
  } else if (!data.body) {
    return { error: true, errorText: t(`reports:"Body" is required`) };
  } else if (!data.dateFrom) {
    return { error: true, errorText: t(`reports:"From Date" is required`) };
  }

  return { error: false, errorText: "" };
};

interface EmailFormProps {
  email: string;
  body: string;
  dispatch: React.Dispatch<FormDataAction>;
}

const EmailForm: React.FC<EmailFormProps> = ({ email, body, dispatch }) => {
  const { t } = useTranslation("reports");
  return (
    <>
      <FormGroup css={{ marginBottom: "1rem" }}>
        <FormLabel>
          {t("reports:Send to")}
          <sup>*</sup>
        </FormLabel>
        <FormControl
          defaultValue={email}
          onChange={(e) => dispatch({ type: "EMAIL", payload: e.target.value })}
        />
        <FormText muted style={{ fontSize: "80%" }}>
          {t(
            "reports:Multiple emails can be entered by separating each with a semicolon",
          )}
        </FormText>
      </FormGroup>
      <FormGroup css={{ marginBottom: "1rem" }}>
        <FormLabel>
          {t("reports:Body")}
          <sup>*</sup>
        </FormLabel>
        <FormControl
          as="textarea"
          defaultValue={body}
          onChange={(e) => dispatch({ type: "BODY", payload: e.target.value })}
        />
        <FormText
          muted
          style={{ display: "flex", flexDirection: "column", fontSize: "80%" }}
        >
          <span css={{ fontWeight: "bold" }}>{t("reports:Notes")}:</span>
          <span css={{ marginLeft: "0.25rem" }}>
            {t("reports:Large data files may fail to send.")}
          </span>
          <span css={{ marginLeft: "0.25rem" }}>
            {t(
              "reports:Last tab of the report must contain the data table you want exported.",
            )}
          </span>
        </FormText>
      </FormGroup>
    </>
  );
};

interface RepeatTimeFormProps {
  data: FormData;
  dispatch: React.Dispatch<FormDataAction>;
}

const RepeatTimeForm: React.FC<RepeatTimeFormProps> = ({ data, dispatch }) => {
  const { t } = useTranslation("reports");

  let otherTimes = [...data.time];
  let firstTime;
  if (otherTimes.length > 0) {
    firstTime = otherTimes.shift();
  }

  const handleRepeatInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    dispatch({
      type: "REPEAT",
      payload: (
        e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      ).value,
    });
  };

  const handleTimeChange = (index: number, time: string) => {
    dispatch({ type: "TIME", payload: { index, time } });
  };

  const handleAddTime = () => {
    dispatch({ type: "ADD_TIME" });
  };

  const handleRemoveTime = (index: number) => {
    dispatch({ type: "REMOVE_TIME", payload: { index } });
  };

  let repeatInput;
  if (data.pattern.startsWith("yearly")) {
    repeatInput = (
      <FormControl
        as="select"
        value={data.repeatMonth}
        onChange={handleRepeatInputChange}
      >
        <option value="0">{t("reports:January")}</option>
        <option value="1">{t("reports:February")}</option>
        <option value="2">{t("reports:March")}</option>
        <option value="3">{t("reports:April")}</option>
        <option value="4">{t("reports:May")}</option>
        <option value="5">{t("reports:June")}</option>
        <option value="6">{t("reports:July")}</option>
        <option value="7">{t("reports:August")}</option>
        <option value="8">{t("reports:September")}</option>
        <option value="9">{t("reports:October")}</option>
        <option value="10">{t("reports:November")}</option>
        <option value="11">{t("reports:December")}</option>
      </FormControl>
    );
  } else {
    repeatInput = (
      <FormControl
        style={{ width: "4.5em" }}
        type="number"
        min="1"
        value={data.repeat}
        onChange={handleRepeatInputChange}
      />
    );
  }

  return (
    <FormGroup
      className="d-flex flex-column"
      css={{ marginBottom: "1rem", width: "max-content" }}
    >
      <div
        className="d-flex align-items-center mb-1 align-self-start"
        style={{ position: "relative" }}
      >
        <span className="mb-0 me-1 ms-1 flex-shrink-0">Repeats every</span>
        {repeatInput}

        <span className="mb-0 me-1 ms-1 flex-shrink-0">
          {data.pattern === "daily" && t("reports:day(s) at")}
          {data.pattern === "weekly" && t("reports:week(s) at")}
          {data.pattern.startsWith("monthly") && t("reports:month(s) at")}
          {data.pattern.startsWith("yearly") && t("reports:at")}
        </span>

        <TimePicker
          css={{ minWidth: 170 }}
          showSecond={false}
          value={moment(firstTime, "HH:mm:ss")}
          format={"h:mm a"}
          onChange={(value) => handleTimeChange(0, value.format("HH:mm:ss"))}
          use12Hours
          allowEmpty={false}
        />

        <Button className="ms-1" onClick={() => handleAddTime()}>
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </div>
      {otherTimes.map((time, timeIndex) => {
        return (
          <div
            key={timeIndex}
            className="d-flex align-items-center mb-1 align-self-end"
            style={{ position: "relative" }}
          >
            <span className="mb-0 me-1 flex-shrink-0">and</span>

            <TimePicker
              css={{ minWidth: 170 }}
              showSecond={false}
              value={moment(time, "HH:mm:ss")}
              format={"h:mm a"}
              onChange={(value) => {
                handleTimeChange(timeIndex + 1, value.format("HH:mm:ss"));
              }}
              use12Hours
              allowEmpty={false}
            />

            <Button
              className="ms-1"
              variant="danger"
              onClick={() => {
                handleRemoveTime(timeIndex + 1);
              }}
            >
              <FontAwesomeIcon icon={faMinus} />
            </Button>
          </div>
        );
      })}
    </FormGroup>
  );
};

interface DaysCheckboxFormProps {
  data: FormData;
  dispatch: React.Dispatch<FormDataAction>;
}

const DaysCheckboxForm: React.FC<DaysCheckboxFormProps> = ({
  data,
  dispatch,
}) => {
  const { t } = useTranslation("reports");

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const checked = e.target.checked;
    if (checked) {
      dispatch({ type: "ADD_WEEKDAY", payload: { value } });
    } else {
      dispatch({ type: "REMOVE_WEEKDAY", payload: { value } });
    }
  };

  return (
    <FormGroup
      className="d-flex align-items-center flex-wrap"
      css={{ marginBottom: "1rem" }}
    >
      <div style={{ display: "flex" }}>
        {(data.pattern === "monthly_weekday" ||
          data.pattern === "yearly_weekday") && (
          <>
            <span
              style={{ flexShrink: "0", alignSelf: "center" }}
              className="me-1"
            >
              {t("reports:On the")}
            </span>
            <FormControl
              as="select"
              className="me-3"
              style={{ width: "unset", alignSelf: "center" }}
              value={data.weekNum}
              onChange={(e) =>
                dispatch({ type: "WEEK_NUM", payload: e.target.value })
              }
            >
              <option value="1">{t("reports:First")}</option>
              <option value="2">{t("reports:Second")}</option>
              <option value="3">{t("reports:Third")}</option>
              <option value="4">{t("reports:Fourth")}</option>
              <option value="5">{t("reports:Fifth")}</option>
            </FormControl>
          </>
        )}

        {data.pattern === "weekly" && (
          <span className="me-3">{t("reports:On")}</span>
        )}

        <div
          style={{
            display: "flex",
            flex: "column",
            flexWrap: "wrap",
          }}
        >
          <FormCheck
            id="cbMon"
            style={{ minWidth: "15%" }}
            type="checkbox"
            inline
            label={t("reports:Mon")}
            value="1"
            checked={data.weekday.includes("1")}
            onChange={handleCheckboxChange}
          />
          <FormCheck
            id="cbTue"
            style={{ minWidth: "15%" }}
            type="checkbox"
            inline
            label={t("reports:Tue")}
            value="2"
            checked={data.weekday.includes("2")}
            onChange={handleCheckboxChange}
          />
          <FormCheck
            id="cbWed"
            style={{ minWidth: "15%" }}
            type="checkbox"
            inline
            label={t("reports:Wed")}
            value="3"
            checked={data.weekday.includes("3")}
            onChange={handleCheckboxChange}
          />
          <FormCheck
            id="cbThu"
            style={{ minWidth: "15%" }}
            type="checkbox"
            inline
            label={t("reports:Thu")}
            value="4"
            checked={data.weekday.includes("4")}
            onChange={handleCheckboxChange}
          />
          <FormCheck
            id="cbFri"
            style={{ minWidth: "15%" }}
            type="checkbox"
            inline
            label={t("reports:Fri")}
            value="5"
            checked={data.weekday.includes("5")}
            onChange={handleCheckboxChange}
          />
          <FormCheck
            id="cbSat"
            style={{ minWidth: "15%" }}
            type="checkbox"
            inline
            label={t("reports:Sat")}
            value="6"
            checked={data.weekday.includes("6")}
            onChange={handleCheckboxChange}
          />
          <FormCheck
            id="cbSun"
            style={{ minWidth: "15%" }}
            type="checkbox"
            inline
            label={t("reports:Sun")}
            value="0"
            checked={data.weekday.includes("0")}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>
    </FormGroup>
  );
};

const getNthWeekdayOfMonth = (
  baseMoment: moment.Moment,
  n: number,
  weekday: string,
): moment.Moment => {
  const firstWeekdayInMonth = baseMoment.clone().day(parseInt(weekday));

  if (firstWeekdayInMonth.month() !== baseMoment.month()) {
    firstWeekdayInMonth.add(1, "week");
  }

  return firstWeekdayInMonth.clone().add(n, "weeks");
};

function* createSampleDateTimeGenerator(data: FormData) {
  const {
    pattern,
    repeat,
    repeatMonth,
    weekday: weekdays = [],
    weekNum,
    onDay,
    time = [],
    dateFrom,
    dateTo,
  } = data;

  const times = [...time].sort();

  const startDateTime = moment(`${dateFrom}T${_.min(times)}`);
  const endDateTime = dateTo ? moment(`${dateTo}T${_.max(times)}`) : null;

  let invalidCount = 0;

  const isAfterStartDate = (dateTimeToTest: moment.Moment) => {
    const isBeforeNow = dateTimeToTest.isBefore(moment.now());
    const isBeforeEarliestDateTime = dateTimeToTest.isBefore(startDateTime);
    return !isBeforeNow && !isBeforeEarliestDateTime;
  };

  const isBeforeEndDate = (dateTimeToTest: moment.Moment) => {
    if (_.isNil(endDateTime)) {
      return true;
    }

    const isAfterLatestDateTime = dateTimeToTest.isAfter(endDateTime);
    return !isAfterLatestDateTime;
  };

  const isValidSample = (dateTimeToTest: moment.Moment) => {
    const isValid =
      isAfterStartDate(dateTimeToTest) && isBeforeEndDate(dateTimeToTest);
    if (!isValid) {
      invalidCount += 1;
    }
    return isValid;
  };

  const baseDateTimes = times.map((time) => moment(`${dateFrom}T${time}`));

  const repeatNum = parseInt(repeat, 10);
  const onDayNum = parseInt(onDay, 10);
  const repeatMonthNum = parseInt(repeatMonth, 10);
  const weekInMonthNum = parseInt(weekNum, 10);

  while (true) {
    if (invalidCount >= 1000) {
      break;
    }

    if (pattern === "daily") {
      for (const sampleMoment of baseDateTimes) {
        if (isValidSample(sampleMoment)) {
          yield sampleMoment.clone();
        }
      }

      baseDateTimes.forEach((timeMoment) => timeMoment.add(repeatNum, "days"));
    } else if (pattern === "weekly") {
      if (weekdays.length === 0) {
        break;
      }

      const weekdayDateTimes = weekdays
        .map((dayOfWeek) =>
          baseDateTimes.map((m) => m.clone().day(parseInt(dayOfWeek))),
        )
        .flat();

      for (const sampleMoment of weekdayDateTimes) {
        if (isValidSample(sampleMoment)) {
          yield sampleMoment.clone();
        }
      }

      baseDateTimes.forEach((timeMoment) => timeMoment.add(repeatNum, "weeks"));
    } else if (pattern === "monthly_day") {
      const monthlyDateTimes = baseDateTimes.map((m) =>
        m.clone().date(onDayNum),
      );

      for (const sampleMoment of monthlyDateTimes) {
        if (isValidSample(sampleMoment)) {
          yield sampleMoment.clone();
        }
      }

      baseDateTimes.forEach((timeMoment) =>
        timeMoment.add(repeatNum, "months"),
      );
    } else if (pattern === "monthly_weekday") {
      if (weekdays.length === 0) {
        break;
      }

      for (const weekday of weekdays) {
        for (const baseMoment of baseDateTimes) {
          const firstDayOfMonth = baseMoment.clone().date(1);

          const nthWeekdayInMonth = getNthWeekdayOfMonth(
            firstDayOfMonth,
            weekInMonthNum - 1,
            weekday,
          );

          /* Check that nth weekday falls after the month ends
           * If it does, yield that result
           * Note: We might want default to the last weekday if this happens
           *       if we don't, we will skip a year
           */
          if (nthWeekdayInMonth.month() === firstDayOfMonth.month()) {
            if (isValidSample(nthWeekdayInMonth)) {
              yield nthWeekdayInMonth.clone();
            }
          }
        }
      }

      baseDateTimes.forEach((timeMoment) =>
        timeMoment.add(repeatNum, "months"),
      );
    } else if (pattern === "yearly_day") {
      const monthlyDateTimes = baseDateTimes.map((m) =>
        m.clone().month(repeatMonthNum).date(onDayNum),
      );

      for (const sampleMoment of monthlyDateTimes) {
        if (isValidSample(sampleMoment)) {
          yield sampleMoment.clone();
        }
      }

      baseDateTimes.forEach((timeMoment) => timeMoment.add(repeatNum, "years"));
    } else if (pattern === "yearly_weekday") {
      if (weekdays.length === 0) {
        break;
      }

      for (const weekday of weekdays) {
        for (const baseMoment of baseDateTimes) {
          const firstDayOfMonth = baseMoment
            .clone()
            .month(repeatMonthNum)
            .date(1);

          const nthWeekdayInMonth = getNthWeekdayOfMonth(
            firstDayOfMonth,
            weekInMonthNum - 1,
            weekday,
          );

          /* Check that nth weekday falls after the month ends
           * If it does, yield that result
           * Note: We might want default to the last weekday if this happens
           *       if we don't, we will skip a year
           */
          if (nthWeekdayInMonth.month() === firstDayOfMonth.month()) {
            if (isValidSample(nthWeekdayInMonth)) {
              yield nthWeekdayInMonth.clone();
            }
          }
        }
      }

      baseDateTimes.forEach((timeMoment) => timeMoment.add(repeatNum, "years"));
    } else {
      break;
    }
  }

  yield null;
}

const getSampleSchedule = (data: FormData) => {
  const sampleGenerator = createSampleDateTimeGenerator(data);

  const sampleSchedule: string[] = [];

  for (let i = 0; i < 5; i++) {
    const nextTime = sampleGenerator.next();

    if (nextTime.done) {
      break;
    }

    if (nextTime.value) {
      sampleSchedule.push(nextTime.value.format("ddd, MMMM D, YYYY hh:mm a"));
    }
  }

  return sampleSchedule
    .filter((item) => item !== null)
    .sort((a, b) => {
      const dateA = moment(a, "ddd, MMMM D, YYYY hh:mm a");
      const dateB = moment(b, "ddd, MMMM D, YYYY hh:mm a");
      return dateA.isBefore(dateB) ? -1 : 1;
    });
};

export const EmailScheduleModal: React.FC<{
  show: boolean;
  hide: () => void;
  report: ReportDTO;
  schedule?: EmailScheduleDTO;
}> = ({ show, hide, report, schedule }) => {
  const { t } = useTranslation("reports");
  const {
    createSchedule,
    updateSchedule,
    deleteSchedule,
    isCreating,
    isUpdating,
    isDeleting,
    createError,
    updateError,
    deleteError,
  } = useEmailSchedule();

  const [data, dispatch] = useReducer(
    formDataReducer,
    getInitialScheduleState(),
  );

  const [errorText, setErrorText] = useState("");
  const [showError, setShowError] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    if (show === false) {
      dispatch({ type: "RESET", payload: getInitialScheduleState() });
      setShowError(false);
      setShowDeleteConfirmation(false);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      if (schedule) {
        dispatch({ type: "LOAD_SCHEDULE", payload: schedule });
      } else {
        dispatch({ type: "RESET", payload: getInitialScheduleState() });
      }
    }
  }, [schedule, show]);

  useEffect(() => {
    if (createError || updateError || deleteError) {
      setErrorText(
        (createError || updateError || deleteError)?.message ||
          t("reports:Something went wrong"),
      );
      setShowError(true);
    }
  }, [createError, updateError, deleteError, t]);

  const showDaysCheckboxes =
    data.pattern === "weekly" ||
    data.pattern === "monthly_weekday" ||
    data.pattern === "yearly_weekday";

  const isProcessingRequest = isCreating || isUpdating || isDeleting;

  const handleSubmit = async () => {
    const { error, errorText } = validateScheduleForm(data, t);
    if (error) {
      setErrorText(errorText);
      setShowError(true);
      return;
    }

    const requestData = {
      name: data.name,
      email: formatEmail(data.email),
      emailBody: data.body,
      details: getPatternDetails(data),
      enabled: !data.paused,
    };

    try {
      if (schedule) {
        await updateSchedule(report, schedule.id, {
          ...requestData,
          id: schedule.id,
        });
      } else {
        await createSchedule(report, requestData);
      }

      dispatch({ type: "RESET", payload: getInitialScheduleState() });
      hide();
    } catch (error) {
      console.error("Error submitting schedule:", error);

      if (error instanceof DuplicateScheduleError) {
        setErrorText(error.message);
        setShowError(true);
        return;
      }

      setErrorText(
        error instanceof Error
          ? error.message
          : t(
              "reports:An unexpected error occurred while creating the schedule.",
            ),
      );
      setShowError(true);
    }
  };

  const handleDelete = async () => {
    if (!schedule) return;

    try {
      const success = await deleteSchedule(report, schedule.id);
      if (success) {
        dispatch({ type: "RESET", payload: getInitialScheduleState() });
        hide();
      }
    } catch (error) {
      console.error("Error deleting schedule:", error);

      setErrorText(
        error instanceof Error
          ? error.message
          : t(
              "reports:An unexpected error occurred while deleting the schedule.",
            ),
      );
      setShowError(true);
    }
  };

  const confirmDelete = () => {
    setShowDeleteConfirmation(true);
  };

  let submitButtonText;
  if (schedule) {
    submitButtonText = isUpdating
      ? t("reports:Saving...")
      : t("reports:Save Schedule");
  } else {
    submitButtonText = isCreating
      ? t("reports:Creating...")
      : t("reports:Create Schedule");
  }

  return (
    <>
      <Modal
        backdrop="static"
        show={show}
        onHide={() => hide()}
        size="lg"
        {...({} as any)}
      >
        <Modal.Header
          title={
            schedule
              ? t("reports:Update Email Schedule")
              : t("reports:Create Email Schedule")
          }
        />
        <Modal.Body {...({} as any)}>
          {showError && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setShowError(false)}
            >
              {errorText}
            </Alert>
          )}
          <FormGroup css={{ marginBottom: "1rem" }}>
            <div className="d-flex justify-content-between">
              <div>
                <FormLabel>{t("reports:Schedule Name")}</FormLabel>
                <sup>*</sup>
              </div>
              <FormCheck
                id="pause_schedule"
                label={t("reports:Pause")}
                checked={data.paused}
                onChange={(e) =>
                  dispatch({ type: "PAUSE", payload: e.target.checked })
                }
              />
            </div>

            <FormControl
              value={data.name}
              onChange={(e) =>
                dispatch({ type: "NAME", payload: e.target.value })
              }
            />
          </FormGroup>
          <EmailForm email={data.email} body={data.body} dispatch={dispatch} />
          <hr />
          <div className="d-flex flex-column flex-lg-row">
            <div className="flex-lg-grow-1">
              <FormGroup css={{ marginBottom: "1rem" }}>
                <FormLabel className="fw-bold">
                  {t("reports:Repeating Pattern")}:
                </FormLabel>
                <FormControl
                  as="select"
                  value={data.pattern}
                  onChange={(e) => {
                    dispatch({ type: "PATTERN", payload: e.target.value });
                  }}
                >
                  <option value="daily">{t("reports:Daily")}</option>
                  <option value="weekly">{t("reports:Weekly")}</option>
                  <option value="monthly_day">
                    {t("reports:Monthly (Specific Day)")}
                  </option>
                  <option value="monthly_weekday">
                    {t("reports:Monthly (Day of Week)")}
                  </option>
                  <option value="yearly_day">
                    {t("reports:Yearly (Specific Day of Month)")}
                  </option>
                  <option value="yearly_weekday">
                    {t("reports:Yearly (Day of Week in Month)")}
                  </option>
                </FormControl>
              </FormGroup>
              <RepeatTimeForm data={data} dispatch={dispatch} />
              {showDaysCheckboxes && (
                <DaysCheckboxForm data={data} dispatch={dispatch} />
              )}
              {(data.pattern === "monthly_day" ||
                data.pattern === "yearly_day") && (
                <FormGroup
                  className="d-flex align-items-center"
                  css={{ marginBottom: "1rem" }}
                >
                  <span>{t("reports:On day")}</span>
                  <FormControl
                    style={{ width: "4em" }}
                    className="ms-1 me-1"
                    type="number"
                    min="1"
                    max="31"
                    defaultValue={data.onDay || 1}
                    onChange={(e) =>
                      dispatch({ type: "ON_DAY", payload: e.target.value })
                    }
                  />
                  {data.pattern === "monthly_day" && (
                    <span>{t("reports:of each month")}</span>
                  )}
                  {data.pattern === "yearly_day" && (
                    <span>{t("reports:of that month")}</span>
                  )}
                </FormGroup>
              )}
              <FormGroup className="d-flex align-items-center flex-wrap mb-1">
                <span className="me-1">{t("reports:From")}</span>
                <FormControl
                  style={{ width: "unset" }}
                  type="date"
                  value={data.dateFrom}
                  min={getCurrentDateForInput()}
                  onChange={(e) =>
                    dispatch({ type: "DATE_FROM", payload: e.target.value })
                  }
                />
                <span style={{ margin: "0 0.25em 0 0.3em" }}>
                  {t("reports:to")}
                </span>
                <FormControl
                  style={{ width: "unset" }}
                  type="date"
                  value={data.dateTo}
                  min={data.dateFrom ?? getCurrentDateForInput()}
                  onChange={(e) =>
                    dispatch({ type: "DATE_TO", payload: e.target.value })
                  }
                />
              </FormGroup>
            </div>
            <div
              className="mt-4 mt-lg-0 mb-4 mb-lg-0 me-lg-4 ms-lg-4"
              css={{
                borderTop: "1px solid rgba(0,0,0,0.1)",
                [MediaQueries.largeAndUp]: {
                  borderRight: "1px solid rgba(0,0,0,0.1)",
                },
              }}
            />
            <div css={{ minWidth: 225 }}>
              <div className="mb-2 fw-bold">
                {t("reports:Sample Schedule")}:
              </div>
              {show &&
                getSampleSchedule(data).map((example, index) => (
                  <div key={index}>{example}</div>
                ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {schedule && (
            <FormButton
              style={{ marginRight: "auto" }}
              actionType={null}
              label={
                isDeleting ? t("reports:Deleting...") : t("reports:Delete")
              }
              disabled={isProcessingRequest}
              clickHandler={confirmDelete}
            />
          )}
          <FormButton
            actionType={null}
            label={submitButtonText}
            disabled={isProcessingRequest}
            clickHandler={handleSubmit}
          />
          <FormButton
            label={t("reports:Cancel")}
            disabled={isProcessingRequest}
            clickHandler={() => hide()}
          />
        </Modal.Footer>
      </Modal>

      <DialogModal
        show={showDeleteConfirmation}
        onHide={() => setShowDeleteConfirmation(false)}
        title={null}
        cancelButtonText={t("reports:Cancel")}
        submitButtonText={t("reports:Delete")}
        submitButtonVariant="danger"
        onSubmit={handleDelete}
        data-qa="delete-schedule-confirmation-dialog"
      >
        <div css={{ padding: "10px 0" }}>
          {t("reports:Are you sure you want to delete this schedule?")}
        </div>
      </DialogModal>
    </>
  );
};
